import { useCallback, useEffect, useMemo, useState } from 'react';

import { ExportColumn } from '~utils/export/exportColumn';

const makeStorageKey = (surfaceId: string) => `${surfaceId}_exportColumns`;

function readInitialSelection<T>(storageKey: string, columns: ExportColumn<T>[]) {
  const defaults = columns
    .filter((column) => column.defaultSelected)
    .map((column) => column.id);
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return defaults;
    const parsed = JSON.parse(stored) as unknown;
    if (!Array.isArray(parsed)) return defaults;
    const knownIds = new Set(columns.map((column) => column.id));
    const validIds = parsed.filter(
      (id): id is string => typeof id === 'string' && knownIds.has(id),
    );
    return validIds.length > 0 ? validIds : defaults;
  } catch {
    return defaults;
  }
}

export default function useColumnSelection<T>(
  surfaceId: string,
  columns: ExportColumn<T>[],
) {
  const storageKey = makeStorageKey(surfaceId);
  // Ordered array of selected column ids; it encodes both selection and export order.
  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    readInitialSelection(storageKey, columns),
  );

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(selectedIds));
  }, [storageKey, selectedIds]);

  const move = useCallback((fromIndex: number, toIndex: number) => {
    setSelectedIds((ids) => {
      const reordered = ids.slice();
      const [movedId] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, movedId);
      return reordered;
    });
  }, []);

  const reset = useCallback(() => {
    setSelectedIds(
      columns.filter((column) => column.defaultSelected).map((column) => column.id),
    );
  }, [columns]);

  const selectAll = useCallback(() => {
    setSelectedIds((ids) => [
      ...ids,
      ...columns.filter((column) => !ids.includes(column.id)).map((column) => column.id),
    ]);
  }, [columns]);

  const toggle = useCallback((columnId: string) => {
    setSelectedIds((ids) =>
      ids.includes(columnId) ? ids.filter((id) => id !== columnId) : [...ids, columnId],
    );
  }, []);

  const orderedSelectedColumns = useMemo(() => {
    const columnsById = new Map(columns.map((column) => [column.id, column]));
    return selectedIds.flatMap((id) => columnsById.get(id) ?? []);
  }, [columns, selectedIds]);

  return { move, orderedSelectedColumns, reset, selectAll, selectedIds, toggle };
}
